// Overview — the first screen, and the answer to "is anything wrong right now?"
//
// Ordered by what a superuser opening this on a phone needs first: anything
// broken, then load, then the standing tenant/user counts. Everything on it is
// a link into the screen that can act on it — a stat you cannot drill into is
// a number you have to go looking for twice.

import { api, errorText } from '../api';
import { useAsync } from '../useAsync';
import { navigate } from '../router';
import { ago, num, total } from '../format';
import Sparkline from '../ui/Sparkline';
import {
  Card, ErrorState, KV, Note, PageHead, Pill, RefreshButton, Skeleton, Stat,
} from '../ui/primitives';

export default function Overview() {
  const ov = useAsync(() => api.overview(24), []);
  const act = useAsync(() => api.activity(14), []);

  const reloadAll = () => { ov.reload(); act.reload(); };
  const d = ov.data;

  return (
    <>
      <PageHead
        title="Overview"
        sub={d ? `Last 24 hours · ${ago(d.generated_at)}` : 'Platform health and load'}
        action={<RefreshButton onClick={reloadAll} loading={ov.loading} at={ov.at} />}
      />

      {ov.error && (
        <ErrorState error={ov.error} text={errorText(ov.error)} onRetry={ov.reload} />
      )}
      {!d && ov.loading && <Skeleton rows={4} />}

      {d && (
        <>
          {/* Anything actually wrong goes above the numbers. */}
          <div className="stack" style={{ marginBottom: 16 }}>
            {d.platform.db?.ok === false && (
              <Note tone="danger" title="Database unreachable">
                The API is up but cannot reach its database
                {d.platform.db?.error ? `: ${d.platform.db.error}` : '.'} Nothing
                that reads or writes data will work until this clears.
              </Note>
            )}
            {!d.platform.sso_configured && (
              <Note tone="danger" title="SSO not configured">
                GOOGLE_CLIENT_ID is unset on the backend — no one can sign in.
              </Note>
            )}
            {!d.platform.auth_required && (
              <Note tone="warn" title="AUTH_REQUIRED is off">
                Business routes on the backend are open to unauthenticated
                callers. Expected before the auth flip; a problem after it.
              </Note>
            )}
            {d.jobs.failed > 0 && (
              <Note tone="warn" title={`${d.jobs.failed} job${d.jobs.failed === 1 ? '' : 's'} failed in 24h`}>
                <button className="btn small" onClick={() => navigate('/monitor?status=failed')}>
                  Show failures
                </button>
              </Note>
            )}
          </div>

          <section className="section">
            <h2>Right now</h2>
            <div className="grid grid-2">
              <Stat label="Open jobs" value={num(total(d.jobs.open))}
                    foot={Object.entries(d.jobs.open).map(([k, v]) => `${v} ${k}`).join(' · ') || 'idle'}
                    onClick={() => navigate('/monitor?open=1')} />
              <Stat label="Failed 24h" value={num(d.jobs.failed)}
                    alert={d.jobs.failed > 0}
                    foot="click to inspect"
                    onClick={() => navigate('/monitor?status=failed')} />
              <Stat label="Signed in" value={num(d.users.with_active_session)}
                    foot={`${d.events.logins} logins in 24h`} />
              <Stat label="Approvals" value={num(d.approvals.pending)}
                    foot="pending decision" />
            </div>
          </section>

          <section className="section">
            <h2>Tenants</h2>
            <div className="grid grid-2">
              <Stat label="Orgs" value={num(d.orgs.total)}
                    foot={d.orgs.inactive ? `${d.orgs.inactive} deactivated` : 'all active'}
                    alert={d.orgs.inactive > 0}
                    onClick={() => navigate('/orgs')} />
              <Stat label="Users" value={num(d.users.total)}
                    foot={`${d.users.active} active`}
                    onClick={() => navigate('/users')} />
              <Stat label="Never signed in" value={num(d.users.never_signed_in)}
                    foot="provisioned, not bound"
                    onClick={() => navigate('/users?status=unbound')} />
              <Stat label="Superusers" value={num(d.users.superusers)}
                    foot="seed-only flag" />
            </div>
          </section>

          <section className="section">
            <Card title="Activity · 14 days"
                  action={<span className="mono dim" style={{ fontSize: 10 }}>events</span>}>
              {act.data
                ? <Sparkline buckets={act.data.buckets} valueKey="events"
                             alertKey="jobs_failed" label="events per day" />
                : <div className="skeleton" style={{ height: 46 }} />}
              <div className="dim" style={{ fontSize: 11, marginTop: 8 }}>
                Red dots mark days with job failures.
              </div>
            </Card>
          </section>

          <section className="section">
            <Card title="Platform">
              <KV items={[
                ['App', <span key="a" className="mono">{d.platform.app_version}</span>],
                ['Engine', <span key="e" className="mono">{d.platform.engine_version || '—'}</span>],
                ['Harness', <span key="h" className="mono">{d.platform.harness_version || '—'}</span>],
                ['Database', <Pill key="d" tone={d.platform.db?.ok ? 'ok' : 'danger'}>
                  {d.platform.db?.ok ? 'connected' : 'down'}
                </Pill>],
                ['Blob store', <Pill key="b" tone={d.platform.blob?.configured ? 'ok' : 'warn'}>
                  {d.platform.blob?.configured ? (d.platform.blob.kind || 'configured') : 'unset'}
                </Pill>],
                ['Auth enforced', <Pill key="r" tone={d.platform.auth_required ? 'ok' : 'warn'}>
                  {d.platform.auth_required ? 'yes' : 'no'}
                </Pill>],
              ]} />
            </Card>
          </section>
        </>
      )}
    </>
  );
}
